import Cookies from "js-cookie";
import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { CoreKeys } from "@intelligems/ig-plugin/core";
import { MessageTypeList, UserAlertsDto } from "@intelligems/ig-types";
import { Box } from "@mui/material";
import { useSwitchOrganization } from "@src/api/organizations/switch-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { usePatchUser } from "@src/api/users/patch-user";
import { useVersionCheck } from "@src/api/versions/use-version-check";
import { UpgradeProfitOptimizationDialog } from "@src/components/Dialogs/UpgradeProfitOptimizationDialog";
import { ModuleTrialWelcomeDialog } from "@src/features/sharedExperience/components/alertsAndWarnings/ModuleTrialWelcomeDialog";
import { ForcePasswordReset } from "@src/components/ForcePasswordReset/ForcePasswordReset";
import { ViewAs } from "@src/components/Navigation/ViewAs/ViewAs";
import { StandardRoutes } from "@src/components/Utils/routes";
import { cancelActiveAgenticLogicCreation } from "@src/features/ai/components/AiChat/agenticLogicCreationController";
import { isAtlasChatLocation } from "@src/features/ai/pages/agentic-logic/surface/surface";
import { useActiveAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useActiveAgenticLogicSurface";
import {
  clearSyntheticCategorySwap,
  isSyntheticCategorySwapPending,
} from "@src/features/experiments/hooks/experience-route-swap-flag";
import { useDialogs } from "@src/hooks/dialog";
import { useDocumentTitle } from "@src/hooks/useDocumentTitle";
import { useSelector } from "@src/store";
import { clearAiChat } from "@src/store/slices/aiChat";
import { setAiChat } from "@src/store/slices/global";
import { initOrganization } from "@src/store/slices/organization";
import { initUser } from "@src/store/slices/user";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

import { useGetOrg } from "../api/organizations/get-organization";
import { useGetOrgList } from "../api/organizations-list/get-organization-list";
import { useUser } from "../api/user-auths/login";
import { ScopedDialog } from "../components/Dialogs/ScopedDialog";
import { SetDomainDialog } from "../components/Dialogs/SetDomainDialog";
import { usePrefetchQueries } from "../hooks/prefetchQueries";
import { useQueryParam } from "../hooks/queryParams";
import { appendNewOrg } from "../store/slices/appSettings";
import { Logout } from "../utils";
import { IgStorage } from "../utils/igStorage";
import Loading from "../utils/Loading";

import { AppConfetti } from "./AppConfetti";
import { DialogProvider } from "./DialogProvider";
import { WebsocketProvider } from "./WebsocketProvider";

interface LoggedInProviderProps {
  children?: ReactNode;
}

export const LoggedInProvider = ({ children }: LoggedInProviderProps) => {
  usePrefetchQueries();
  useVersionCheck();
  useDocumentTitle();

  const dispatch = useDispatch();
  const { openDialog, isAnyDialogOpen } = useDialogs();
  const organization = useGetOrg();
  const user = useUser();
  const patchUser = usePatchUser();
  const orgList = useGetOrgList();
  const location = useLocation();
  const navigate = useNavigate();
  const userAlerts = useMemo(() => {
    let userAlerts: Partial<UserAlertsDto> = {};
    if (user.data?.type === "login" && user.data.user.userAlerts) {
      userAlerts = {
        ...user.data.user.userAlerts,
      };
    }
    return userAlerts;
  }, [user.data]);

  const viewedAnalyticsReleasePopup = useMemo(
    () => Cookies.get("viewedAnalyticsReleaseDialog") === "true",
    []
  );

  const viewedWrappedDialog = useMemo(
    () => Cookies.get("viewedWrappedDialog") === "true",
    []
  );

  const isAdmin = useIsAdmin();
  const organizationId = useQueryParam("orgId");
  const shopifyDomain = useQueryParam("shopId");
  const logoutWorker = useRef<SharedWorker | null>(null);
  const switchOrganization = useSwitchOrganization();

  // The experience-creation chat persists its transcript across an
  // accidental close (see `AiChat.tsx`'s `onClose`) — but it shouldn't
  // follow the merchant to an unrelated page or a different experience.
  // This is the single ancestor that stays mounted across ALL route
  // providers (Standard/StandardNoPadding/Onboarding/etc. are its sibling
  // children, each swapped via the router) — the dashboard and an
  // experience page live under two DIFFERENT providers, so tracking this in
  // any one provider's own shell (e.g. `AppLayoutShell`) misses the
  // transition entirely: that shell unmounts and remounts fresh crossing
  // provider boundaries, and a fresh mount's own ref always looks like "no
  // change yet". Scoped to this one mode — see the matching comment in
  // `AiChat.tsx` for why the other chat modes don't get this.
  //
  // Skipped when this navigation itself carries an `aiIntake` hand-off
  // (AskAtlasBuildExperience's own "launch a fresh experience" state):
  // `ExperimentWrapperBody`'s effect opens a brand-new chat with that
  // intake on arrival, and — being a descendant — its effect runs before
  // this one in the same commit. Clearing unconditionally here would win
  // the race and wipe the chat that effect just opened.
  //
  // Also skipped for `useRedirectOnCategorySwitch`'s own internal
  // navigation (personalization <-> experiment for the SAME experience,
  // mid-conversation) — that's a pathname change too, but not the merchant
  // leaving; it's the exact chat this hook re-opens right after.
  //
  // This effect is keyed off `location.pathname` (via `useLocation`), which
  // — for a data router — only updates once the navigation has fully
  // resolved (including any `useBlocker` decision), so by the time this
  // runs, the swap has definitively landed. That makes it the natural,
  // deterministic place to end the synthetic-swap "grace period" — clearing
  // the flag here rather than on a guessed timeout, since this effect is
  // reliably the last of the flag's consumers to see it as still pending.
  const aiChatMode = useSelector((state) => state.global.aiChat.mode);
  const lastAiChatPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (lastAiChatPathnameRef.current === location.pathname) {
      return;
    }
    lastAiChatPathnameRef.current = location.pathname;

    const hasIncomingAiIntake = Boolean(
      (location.state as { aiIntake?: unknown } | null)?.aiIntake
    );
    if (hasIncomingAiIntake) {
      return;
    }
    if (isSyntheticCategorySwapPending()) {
      clearSyntheticCategorySwap();
      return;
    }

    if (aiChatMode === "experience-creation-single-agent") {
      dispatch(clearAiChat());
      dispatch(
        setAiChat([
          { key: "open", value: false },
          { key: "initialContext", value: null },
          { key: "start", value: false },
        ])
      );
    }
  }, [location.pathname, location.state, aiChatMode, dispatch]);

  // The Atlas rail is pinned open on the agentic-logic views where a rule is
  // written — the creation flow and a rule's workspace (see
  // `useAtlasChatRail`). Reaching the rules list, or leaving the surfaces
  // entirely, ends the session and closes the panel.
  //
  // Keyed off pathname AND search, unlike the effect above: those views are
  // query params of one route per surface, so a build handing the author off
  // to the rule it produced never changes the pathname — and the list the
  // panel must close on is that same pathname with the param dropped. Its own
  // effect rather than another branch above, because every other mode there
  // reacts to the pathname alone and must not start firing on query changes.
  // Read here rather than inside the effect: an embedded surface (a rule
  // builder that has taken over a dialog's content) is not in the URL, so the
  // location alone cannot say whether the rail belongs on screen. The provider
  // unregisters on unmount, which is after this effect has run for the
  // location that exits the surface.
  const activeSurface = useActiveAgenticLogicSurface();
  const atlasLocationKey = location.pathname + location.search;
  const lastAtlasLocationKeyRef = useRef(atlasLocationKey);
  useEffect(() => {
    if (lastAtlasLocationKeyRef.current === atlasLocationKey) {
      return;
    }
    lastAtlasLocationKeyRef.current = atlasLocationKey;

    if (aiChatMode !== "agentic-logic-creation") {
      return;
    }
    if (
      isAtlasChatLocation(location.pathname, location.search, activeSurface)
    ) {
      return;
    }

    cancelActiveAgenticLogicCreation("route-leave");
    dispatch(clearAiChat());
    dispatch(
      setAiChat([
        { key: "open", value: false },
        { key: "mode", value: undefined },
        { key: "initialContext", value: null },
        { key: "start", value: false },
        { key: "buildingRuleId", value: null },
        { key: "activeScenario", value: null },
      ])
    );
  }, [
    activeSurface,
    atlasLocationKey,
    location.pathname,
    location.search,
    aiChatMode,
    dispatch,
  ]);

  useEffect(() => {
    try {
      if (!logoutWorker.current) {
        logoutWorker.current = new SharedWorker("./workers/logout-worker.js", {
          type: "module",
          name: "Global Logout",
        });
      }
    } catch (e) {
      console.log("SharedWorker not supported:", e);
    }

    if (logoutWorker.current) {
      logoutWorker.current.port.onmessage = () => {
        Logout();
      };
    }
  }, []);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      const type = e.data?.type;
      if (!MessageTypeList.includes(type)) {
        return null;
      }

      if (type === "LOG_OUT") {
        if (logoutWorker.current) {
          // SharedWorker available - use cross-tab logout
          logoutWorker.current.port.postMessage({ test: "test" });
        } else {
          // SharedWorker not available (Android) - logout directly
          Logout();
        }
      }
    },
    [user.isLoading, user.data]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey) {
        switch (event.key) {
          case "k":
            event.preventDefault();
            openDialog("cmdk");
            break;
          case "s": {
            if (!isAdmin) {
              break;
            }
            event.preventDefault();
            void navigate(StandardRoutes().settings.path);
            break;
          }
        }
      }
    },
    [navigate, isAdmin, dispatch]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (
      organization.data.org.shopId !== null &&
      organization.data.org.name !== null
    ) {
      dispatch(
        appendNewOrg({
          shopId: organization.data.org.shopId!,
          name: organization.data.org.name,
        })
      );
    }
  }, [dispatch, organization.data.org.shopId, organization.data.org.name]);

  useEffect(() => {
    if (
      !isAnyDialogOpen &&
      user.isSuccess &&
      user.data.type === "login" &&
      !user.data.user.showWelcome &&
      userAlerts?.showWrappedDialog !== false &&
      !viewedWrappedDialog
    ) {
      openDialog("intelligemsWrapped");

      Cookies.set("viewedWrappedDialog", "true", {
        expires: CoreKeys.ID_COOKIE_DAYS_TO_LIVE,
        domain: "." + window.location.hostname,
      });

      // Update user alerts in DB to prevent showing again
      patchUser.mutate({
        queryParams: undefined,
        pathParams: { id: user.data.user.id },
        body: {
          user: {
            organizationId: organization.data.org.id,
            userAlerts: {
              ...userAlerts,
              showWrappedDialog: false,
            },
            id: user.data.user.id,
          },
        },
      });
    }
  }, [
    isAnyDialogOpen,
    openDialog,
    organization.data.org.id,
    patchUser,
    userAlerts,
    user.isSuccess,
    user.data,
    viewedWrappedDialog,
  ]);

  useEffect(() => {
    if (user.isSuccess && user.data.type === "login") {
      if (user.data.user.showWelcome) {
        openDialog("onboarding");
      }

      // else if (
      //   (isAdmin && userPopup) ||
      //   (isAdmin &&
      //     userAlerts.showAnalyticsReleaseDialog !== false &&
      //     !viewedAnalyticsReleasePopup)
      // ) {
      //   dispatch(setDialog({ dialog: "showUserInfoPopup", open: true }));
      // }
    }
  }, [
    organization.isSuccess,
    userAlerts,
    user.isSuccess,
    isAdmin,
    viewedAnalyticsReleasePopup,
    user.data,
  ]);

  useEffect(() => {
    if (
      user.isSuccess &&
      user.data &&
      user.data.type === "login" &&
      organization.data &&
      organization.isSuccess
    ) {
      window.pylon = {
        chat_settings: {
          app_id: "67b7d8b3-1e45-4ed0-8349-213619079471",
          email: user.data.user.email,
          name: user.data.user.firstName + " " + user.data.user.lastName,
        },
      };
      window.Pylon("setTicketFormFields", {
        brand: organization.data.org.shopId,
      });
    }
  }, [user.data, user.isSuccess, organization.data]);

  useEffect(() => {
    dispatch(initOrganization(organization.data.org));
  }, [dispatch, organization.data.org]);

  // Mirrors the org above: react-query owns the fetch, the slice is what the
  // rest of the app selects from — a listener or a selector cannot call a query
  // hook, so anything needing the user outside React needs it here.
  useEffect(() => {
    if (user.data?.type === "login") {
      dispatch(initUser(user.data.user));
    }
  }, [dispatch, user.data]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        isAdmin &&
        user.isSuccess &&
        organization.isSuccess &&
        orgList.isSuccess &&
        (organizationId || shopifyDomain)
      ) {
        const currentOrgId = organization.data.org.id;
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete("orgId");
        searchParams.delete("shopId");
        const cleanSearch = searchParams.toString();
        const targetRoute =
          location.pathname + (cleanSearch ? `?${cleanSearch}` : "");

        let targetOrg: any = null;

        if (organizationId) {
          targetOrg = orgList.data.orgs.find(
            (org) => org.id === organizationId
          );
        } else if (shopifyDomain) {
          targetOrg = orgList.data.orgs.find(
            (org) => org.shopId === shopifyDomain
          );
        }

        if (targetOrg && targetOrg.id !== currentOrgId) {
          const token = IgStorage.authToken;
          if (token) {
            await switchOrganization.mutateAsync({
              token,
              orgId: targetOrg.id,
            });
          }
        } else if ((organizationId || shopifyDomain) && !targetOrg) {
          console.error(
            "Organization not found or access denied:",
            organizationId || shopifyDomain
          );
          const cleanUrl = targetRoute || location.pathname;
          await navigate(cleanUrl, { replace: true });
        } else if (organizationId || shopifyDomain) {
          const cleanUrl = targetRoute || location.pathname;
          await navigate(cleanUrl, { replace: true });
        }
      }
    };

    void fetchData();
  }, [
    isAdmin,
    user.isSuccess,
    organization.isSuccess,
    orgList.isSuccess,
    organizationId,
    shopifyDomain,
    organization.data?.org?.id,
    location.pathname,
    location.search,
    navigate,
    orgList.data?.orgs,
  ]);

  if (
    user.isSuccess &&
    user.data.type === "login" &&
    user.data.user.passwordResetRequired
  ) {
    return (
      <Box>
        <ForcePasswordReset />
      </Box>
    );
  }

  return (
    <Box>
      <ViewAs />
      <WebsocketProvider />
      <AppConfetti />
      <ScopedDialog />
      <SetDomainDialog />
      <UpgradeProfitOptimizationDialog />
      <ModuleTrialWelcomeDialog />
      <QueryErrorResetBoundary>
        <Suspense fallback={<Loading />}>
          <DialogProvider />
          {children ? children : <Outlet />}
        </Suspense>
      </QueryErrorResetBoundary>
    </Box>
  );
};

export default LoggedInProvider;
